import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Swords, Gift, Server, Shield, Trophy, Users, Play, RefreshCw,
  Plus, Trash2, Edit3, Check, X, Zap, ArrowRight, Settings, Radio
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Avatar, Username } from "../../components/UserBadge";

export default function IDGArenaTab() {
  const queryClient = useQueryClient();
  const [subTab, setSubTab] = useState("queues"); // 'queues' | 'cases' | 'servers' | 'leaderboard'

  const { data: statusData, refetch: refetchStatus } = useQuery({
    queryKey: ["arenaStatusAdmin"],
    queryFn: async () => (await api.get("/arena/status")).data,
    refetchInterval: 3000
  });

  const { data: casesList = [], refetch: refetchCases } = useQuery({
    queryKey: ["arenaCasesAdmin"],
    queryFn: async () => (await api.get("/arena/cases")).data,
    staleTime: 60000
  });

  const { data: leaderboard = [], refetch: refetchLeaderboard } = useQuery({
    queryKey: ["arenaLeaderboardAdmin"],
    queryFn: async () => (await api.get("/arena/leaderboard?limit=25")).data,
    staleTime: 30000
  });

  const counts = statusData?.counts || {};
  const liveMatches = statusData?.live_matches || [];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-orange-500/15 via-[#0b0e14] to-[#0b0e14] border border-orange-500/30 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 text-xs font-mono font-bold text-orange-400 uppercase tracking-widest mb-1">
            <Swords className="w-4 h-4" /> VultStrike Arena Management System
          </div>
          <h2 className="text-xl font-black uppercase text-white tracking-wide">
            Administrare Matchmaking CS2 & Case Opening
          </h2>
          <p className="text-xs text-zinc-400 mt-1 max-w-xl">
            Gestionează cozile 1v1, 2v2 și 5v5, meciurile în desfășurare, pool-ul de servere dedicate și magazinul de cutii cu skin-uri.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              refetchStatus();
              refetchCases();
              refetchLeaderboard();
              toast.success("Date actualizate!");
            }}
            className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-300 font-mono text-xs font-bold flex items-center gap-2 border border-white/10 transition-all"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reîmprospătează
          </button>
        </div>
      </div>

      {/* Sub Navigation Bar */}
      <div className="flex flex-wrap items-center gap-2 p-1.5 rounded-xl bg-[#090b10] border border-white/5">
        <button
          onClick={() => setSubTab("queues")}
          className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all flex items-center gap-2 ${
            subTab === "queues" ? "bg-[var(--tf-primary)] text-black font-black shadow-md" : "text-zinc-400 hover:text-white"
          }`}
        >
          <Swords className="w-3.5 h-3.5" /> Cozi & Meciuri Live ({liveMatches.length})
        </button>

        <button
          onClick={() => setSubTab("cases")}
          className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all flex items-center gap-2 ${
            subTab === "cases" ? "bg-[var(--tf-primary)] text-black font-black shadow-md" : "text-zinc-400 hover:text-white"
          }`}
        >
          <Gift className="w-3.5 h-3.5" /> Catalog Cutii CS2 ({casesList.length})
        </button>

        <button
          onClick={() => setSubTab("servers")}
          className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all flex items-center gap-2 ${
            subTab === "servers" ? "bg-[var(--tf-primary)] text-black font-black shadow-md" : "text-zinc-400 hover:text-white"
          }`}
        >
          <Server className="w-3.5 h-3.5" /> Pool Servere CS2
        </button>

        <button
          onClick={() => setSubTab("leaderboard")}
          className={`px-4 py-2 rounded-lg text-xs font-bold uppercase transition-all flex items-center gap-2 ${
            subTab === "leaderboard" ? "bg-[var(--tf-primary)] text-black font-black shadow-md" : "text-zinc-400 hover:text-white"
          }`}
        >
          <Trophy className="w-3.5 h-3.5" /> Clasament & Rating ELO
        </button>
      </div>

      {/* SUB-TAB 1: QUEUES & LIVE MATCHES */}
      {subTab === "queues" && (
        <div className="space-y-6">
          {/* Queues Status Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { id: "5v5", name: "5v5 Competitive", count: counts["5v5"] || 0, color: "#FF4F00" },
              { id: "1v1", name: "1v1 Aim Arena", count: counts["1v1"] || 0, color: "#00F0FF" },
              { id: "2v2", name: "2v2 Wingman", count: counts["2v2"] || 0, color: "#a200ff" },
              { id: "retakes", name: "Retakes 3v3", count: counts["retakes"] || 0, color: "#ffaa00" },
            ].map((q) => (
              <div key={q.id} className="p-4 rounded-xl bg-[#090b10] border border-white/5 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-mono text-zinc-500 uppercase block">Coadă Activă</span>
                  <h4 className="text-sm font-bold text-white uppercase">{q.name}</h4>
                </div>
                <div className="text-right">
                  <span className="text-xl font-black" style={{ color: q.color }}>{q.count}</span>
                  <span className="text-[9px] text-zinc-500 font-mono block">jucători</span>
                </div>
              </div>
            ))}
          </div>

          {/* Active Matches Feed */}
          <div className="p-6 rounded-2xl bg-[#090b10] border border-white/5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-black uppercase text-white tracking-wider flex items-center gap-2">
                <Radio className="w-4 h-4 text-red-500 animate-pulse" /> Meciuri Live pe Servere
              </h3>
              <span className="text-xs font-mono text-zinc-400">Total active: <strong className="text-white">{liveMatches.length}</strong></span>
            </div>

            {liveMatches.length === 0 ? (
              <div className="py-12 text-center text-zinc-500 text-xs font-mono">
                Niciun meci activ în acest moment. Meciurile pornesc automat când se strâng jucători în coadă.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {liveMatches.map((m) => (
                  <div key={m.id} className="p-4 rounded-xl bg-black/40 border border-white/10 space-y-3">
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span className="px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 font-bold uppercase">{m.mode}</span>
                      <span className="text-white font-bold">{m.map}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-sky-400 truncate max-w-[120px]">{m.team1_name}</span>
                      <div className="px-3 py-1 rounded bg-black border border-white/10 font-mono font-black text-sm text-white">
                        <span className="text-sky-400">{m.score_ct}</span> : <span className="text-amber-500">{m.score_t}</span>
                      </div>
                      <span className="text-xs font-bold text-amber-500 truncate max-w-[120px] text-right">{m.team2_name}</span>
                    </div>

                    <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[10px] text-zinc-500 font-mono">
                      <span>Status: <strong className="text-emerald-400">{m.status}</strong></span>
                      <span>{m.players_count} Jucători</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SUB-TAB 2: CASES CATALOG */}
      {subTab === "cases" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black uppercase text-white tracking-wider">Cutii CS2 Configurate în Magazin</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {casesList.map((c) => (
              <div key={c.id} className="p-5 rounded-2xl bg-[#090b10] border border-white/10 space-y-4">
                <div className="flex items-center gap-4">
                  <img src={c.image} alt={c.name} className="w-16 h-16 object-contain drop-shadow-md" />
                  <div>
                    <h4 className="text-sm font-black uppercase text-white">{c.name}</h4>
                    <span className="text-xs font-bold text-amber-400 font-mono">{c.price} Puncte / Deschidere</span>
                  </div>
                </div>

                <p className="text-xs text-zinc-400 line-clamp-2">{c.description}</p>

                <div className="pt-3 border-t border-white/5">
                  <span className="text-[10px] font-mono uppercase text-zinc-500 block mb-2">Skin-uri incluse ({c.skins?.length || 0}):</span>
                  <div className="flex flex-wrap gap-1.5">
                    {c.skins?.slice(0, 6).map((s, i) => (
                      <span key={i} className="text-[9px] font-mono px-2 py-0.5 rounded bg-white/5 border border-white/5 text-zinc-300">
                        {s.weapon} {s.name}
                      </span>
                    ))}
                    {c.skins?.length > 6 && (
                      <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-white/5 text-zinc-500">
                        +{c.skins.length - 6} altele
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUB-TAB 3: SERVERS POOL */}
      {subTab === "servers" && (
        <div className="p-6 rounded-2xl bg-[#090b10] border border-white/5 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-black uppercase text-white tracking-wider">Pool Servere Dedicate CS2 (MatchZy / Docker)</h3>
              <p className="text-xs text-zinc-400 mt-0.5">Serverele alocate automat meciurilor competitive când este găsit un meci.</p>
            </div>
          </div>

          <div className="space-y-3">
            {[
              { id: "srv_1", ip: "209.38.247.243", port: 27015, gotv: 27020, status: "ONLINE", location: "București (RO)", matchesCount: 142 },
              { id: "srv_2", ip: "209.38.247.243", port: 27016, gotv: 27021, status: "ONLINE", location: "București (RO)", matchesCount: 98 },
              { id: "srv_3", ip: "209.38.247.243", port: 27017, gotv: 27022, status: "IDLE", location: "București (RO)", matchesCount: 45 },
            ].map((s) => (
              <div key={s.id} className="p-4 rounded-xl bg-black/40 border border-white/10 flex flex-wrap items-center justify-between gap-4 font-mono text-xs">
                <div>
                  <span className="text-white font-bold">{s.ip}:{s.port}</span>
                  <span className="text-zinc-500 block text-[10px]">GOTV: {s.gotv} • Locație: {s.location}</span>
                </div>

                <div className="flex items-center gap-6">
                  <span className="text-zinc-400">Total meciuri: <strong className="text-white">{s.matchesCount}</strong></span>
                  <span className="px-2.5 py-1 rounded bg-emerald-500/20 text-emerald-400 font-bold uppercase text-[10px] border border-emerald-500/30">
                    {s.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUB-TAB 4: LEADERBOARD & MMR */}
      {subTab === "leaderboard" && (
        <div className="p-6 rounded-2xl bg-[#090b10] border border-white/5 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black uppercase text-white tracking-wider">Top Jucători & ELO Rating</h3>
            <span className="text-xs font-mono text-zinc-400">Total înregistrați: <strong className="text-white">{leaderboard.length}</strong></span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-mono">
              <thead>
                <tr className="border-b border-white/10 text-zinc-500 uppercase text-[10px]">
                  <th className="pb-3">#</th>
                  <th className="pb-3">Jucător</th>
                  <th className="pb-3">Rank CS2</th>
                  <th className="pb-3 text-center">Victorii</th>
                  <th className="pb-3 text-center">Winrate</th>
                  <th className="pb-3 text-right">Rating ELO</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {leaderboard.map((u, i) => (
                  <tr key={u.id} className="hover:bg-white/[0.02]">
                    <td className="py-3 font-bold text-zinc-400">{i + 1}</td>
                    <td className="py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar user={u} size={28} hover={true} />
                        <Username user={u} className="font-bold text-xs" />
                      </div>
                    </td>
                    <td className="py-3">
                      <span className="px-2 py-0.5 rounded bg-white/5 text-white text-[10px] font-bold">
                        {u.rank}
                      </span>
                    </td>
                    <td className="py-3 text-center text-zinc-300 font-bold">{u.matches_won}</td>
                    <td className="py-3 text-center text-emerald-400 font-bold">{u.winrate}</td>
                    <td className="py-3 text-right font-bold text-orange-400">{u.elo} ELO</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
